import type {
  ChatMessage,
  ChatError,
  ChatPart,
  ChatStreamPartType,
  ToolUiPart,
  ToolUiState,
  TranscriptMessage,
} from "../types/chat";

/**
 * Pure helpers for reconstructing the client view model from the persisted
 * transcript and the pending-tool marker. Kept free of Vue/Nuxt so the
 * reconstruction rules can be unit-tested in isolation (B10). The stateful
 * client (`useChat`) composes these when loading a conversation.
 */

/**
 * Projects a persisted transcript message into a render-ready {@link ChatMessage}.
 *
 * Three persisted shapes are recognised, in order:
 *  - a rich assistant turn — `partsJson` is a JSON ARRAY of parts (reasoning, tool
 *    input/output, prose) so a reload replays the whole turn, not just the answer;
 *  - a legacy tool-role settlement message — `partsJson` is a single `ToolUiPartDto`
 *    OBJECT, rendered as one read-only tool part;
 *  - plain text/error — no `partsJson`, projected as a single message/error part.
 */
export function transcriptToMessage(message: TranscriptMessage): ChatMessage {
  if (message.partsJson) {
    const parts = parsePartsArray(message.partsJson, message);
    if (parts) {
      return {
        id: message.id,
        role: "assistant",
        createdAt: Date.parse(message.createdAt) || Date.now(),
        parts,
      };
    }
  }

  if (message.role === "tool" && message.partsJson) {
    const tool = parseToolPart(message.partsJson);
    if (tool) {
      return {
        id: message.id,
        role: "assistant",
        createdAt: Date.parse(message.createdAt) || Date.now(),
        parts: [
          {
            key: `tool:${tool.invocationId}`,
            type: "tool",
            text: "",
            isStreaming: false,
            tool,
            sequence: message.sequence,
          },
        ],
      };
    }
  }

  const role: ChatMessage["role"] =
    message.role === "user" || message.role === "system" ? message.role : "assistant";
  return {
    id: message.id,
    role,
    createdAt: Date.parse(message.createdAt) || Date.now(),
    parts: [
      {
        key: `message:${message.id}`,
        type: message.status === "error" ? "error" : "message",
        text: message.content,
        isStreaming: false,
        sequence: message.sequence,
      },
    ],
  };
}

/** A single element of a persisted rich-turn parts array. Mirrors `PersistedTurnPartDto`. */
interface PersistedPart {
  id?: string;
  type?: ChatStreamPartType;
  text?: string | null;
  sequence?: number;
  tool?: (Partial<ToolUiPart> & { state?: string }) | null;
  error?: ChatError | null;
}

const PART_TYPES: ChatStreamPartType[] = ["message", "reasoning", "tool", "error", "resource"];

/**
 * Parses a persisted rich-turn parts array (`PersistedTurnPartDto[]`) into render-ready
 * {@link ChatPart}s. Returns null when `partsJson` is not an array (so the single-object tool form
 * and plain-text fallback still apply), or when no element is renderable.
 */
function parsePartsArray(partsJson: string, message: TranscriptMessage): ChatPart[] | null {
  let raw: unknown;
  try {
    raw = JSON.parse(partsJson);
  } catch {
    return null;
  }
  if (!Array.isArray(raw)) {
    return null;
  }

  const parts: ChatPart[] = [];
  raw.forEach((element, index) => {
    const part = toChatPart(element as PersistedPart, message, index);
    if (part) {
      parts.push(part);
    }
  });
  return parts.length > 0 ? parts : null;
}

function toChatPart(element: PersistedPart, message: TranscriptMessage, index: number): ChatPart | null {
  if (!element || typeof element !== "object") {
    return null;
  }
  const type = element.type;
  if (!type || !PART_TYPES.includes(type) || type === "resource") {
    return null;
  }

  const sequence = typeof element.sequence === "number" ? element.sequence : message.sequence + index;

  if (type === "tool") {
    const tool = element.tool ? normalizeTool(element.tool) : null;
    if (!tool) {
      return null;
    }
    return {
      key: `tool:${tool.invocationId}`,
      type: "tool",
      text: "",
      isStreaming: false,
      tool,
      sequence,
    };
  }

  return {
    key: `${type}:${element.id ?? message.id}:${index}`,
    type,
    text: element.text ?? "",
    isStreaming: false,
    error: type === "error" ? element.error ?? undefined : undefined,
    sequence,
  };
}

/** Parses a persisted tool part (`ToolUiPartDto` JSON) into a {@link ToolUiPart}. */
export function parseToolPart(partsJson: string): ToolUiPart | null {
  try {
    return normalizeTool(JSON.parse(partsJson) as Partial<ToolUiPart> & { state?: string });
  } catch {
    return null;
  }
}

/** Normalizes a parsed tool DTO into a {@link ToolUiPart}, or null when required fields are missing. */
function normalizeTool(dto: (Partial<ToolUiPart> & { state?: string }) | null): ToolUiPart | null {
  if (!dto || typeof dto.invocationId !== "string" || typeof dto.name !== "string") {
    return null;
  }
  return {
    invocationId: dto.invocationId,
    name: dto.name,
    state: (dto.state as ToolUiState) ?? "output-available",
    input: dto.input ?? null,
    output: dto.output,
  };
}

/**
 * Reconstructs the pending interactive tool as an assistant turn carrying the actionable block/card,
 * so a reloaded conversation re-renders the prompt awaiting the user. Returns null when there is no
 * pending tool, the payload is unparseable, or the same invocation is already present in the restored
 * transcript (defensive — the server clears the marker once the tool settles).
 */
export function pendingToolToMessage(
  partsJson: string | null | undefined,
  restored: ChatMessage[],
): ChatMessage | null {
  if (!partsJson) {
    return null;
  }
  const tool = parseToolPart(partsJson);
  if (!tool) {
    return null;
  }
  const alreadyPresent = restored.some((message) =>
    message.parts.some((part) => part.type === "tool" && part.tool?.invocationId === tool.invocationId),
  );
  if (alreadyPresent) {
    return null;
  }
  return {
    id: `pending-tool-${tool.invocationId}`,
    role: "assistant",
    createdAt: Date.now(),
    parts: [
      {
        key: `tool:${tool.invocationId}`,
        type: "tool",
        text: "",
        isStreaming: false,
        tool,
        sequence: Number.MAX_SAFE_INTEGER,
      },
    ],
  };
}

/**
 * Normalizes an unknown thrown value into a user-facing error string, mapping known HTTP status
 * categories to friendly guidance: auth failures (401/403, e.g. an expired session) and not-found
 * (404, e.g. no assistant governs this context). Other errors keep their message.
 */
export function errorMessage(err: unknown): string {
  const status = httpStatusOf(err);
  if (status === 401 || status === 403) {
    return "Your session has expired or you don't have access here. Please sign in again.";
  }
  if (status === 404) {
    return "This assistant isn't available for this page.";
  }
  if (err instanceof Error) {
    return err.message;
  }
  return "Chat request failed.";
}

/** Best-effort HTTP status extraction from a thrown stream error or a `$fetch` `FetchError`. */
function httpStatusOf(err: unknown): number | undefined {
  if (!err || typeof err !== "object") {
    return undefined;
  }
  const e = err as { status?: unknown; statusCode?: unknown; response?: { status?: unknown } };
  const status = e.status ?? e.statusCode ?? e.response?.status;
  return typeof status === "number" ? status : undefined;
}
